// Reports Hub — single page that surfaces all available report tools as a
// tool gallery with eye-catching Odia-flavoured cards. Picking a tool opens
// a period selector (Academic Year → Year → Month → Date optional → Go).
// On Go we compute the same data Generator.jsx computes for that month,
// then render the matching <Reports> subcomponent. Toolbar offers Print /
// PDF / Excel / Word exports against the rendered DOM.
//
// Available to both school users and admin. Admin sees the data of the
// currently-active school (just like Quarterly Reports / Setup).

import { dryRationReportData } from "@/lib/consumption";
import { isClasswiseRiceNorms } from "@/lib/riceNorms";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { isMealDay } from "@/lib/dayKind";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Printer, FileDown, FileSpreadsheet, FileText, Sparkles,
  Receipt, Wheat, Soup, ClipboardCheck, Cookie, Carrot, Sun, Boxes,
} from "lucide-react";
import { toast } from "sonner";
import {
  QUARTERS, createMonth, defaultVendorInfo, money, safeFileName,
} from "@/lib/poshanConstants";
import {
  buildMonthlySummaries, buildTotals, buildAnnexureEntries,
  buildOdiaReceiptGroups, buildIngredientWiseReport,
} from "@/lib/poshanCompute";
import { aggregateDailyToMonthly } from "@/lib/dailyAggregate";
import { buildDayBreakdown } from "@/lib/mealPlan";
import { LADOO_LINES } from "@/lib/mealPlan";
import { dailyApi, vendorApi, cchApi } from "@/lib/setupApi";
import { useActiveSchool } from "@/lib/activeSchool";
import { useRateCardsSync } from "@/lib/useRateCardsSync";
import { useDailyEntriesVersion } from "@/lib/dailyEntriesBus";
import { useMasterDataVersion } from "@/lib/masterDataBus";
import { printReport, downloadPdf } from "@/lib/exporters";
import {
  HandReceiptReport, OdiaReceiptsReport, DryRationReport,
  FsaRiceDistribution, FsaCostDistribution, Ogfr23Report, Ogfr24Report,
  AnnexureReport, IngredientReport,
} from "@/components/poshan/Reports";

// =================================================================
// Tool catalog — each card surfaces in the gallery view. Designed
// for high visual grab: emoji art, gradient accent, Odia tagline.
// =================================================================
const TOOLS = [
  {
    key: "handReceipts",
    title: "Hand Receipts",
    subtitle: "ହାତ ରସିଦ · Category-wise",
    emoji: "🧾",
    art: Receipt,
    grad: "from-amber-100 to-orange-50",
    ring: "ring-amber-400",
    pitch: "ସମସ୍ତ Vendor & Service Receipts — Grocery, Vegetables, Egg, Fuel, Nutrition Ladoo, Ladoo Making, CCH Wages ସବୁ ଏକାଠି। Quarterly Summary + Category-wise Receipts ସମ୍ପୂର୍ଣ୍ଣ pack।",
  },
  {
    key: "dryRation",
    title: "Dry Ration Report",
    subtitle: "ଶୁଖିଲା ରେସନ ବଣ୍ଟନ ବିବରଣୀ",
    emoji: "🌾",
    art: Wheat,
    grad: "from-yellow-100 to-amber-50",
    ring: "ring-yellow-400",
    pitch: "Daywise classwise Rice + Cost ବଣ୍ଟନ ସମ୍ପୂର୍ଣ୍ଣ ବିବରଣୀ + Parent acknowledgement slips।",
  },
  {
    key: "fsaRice",
    title: "FSA · Dry Rice Distribution",
    subtitle: "Food Security Allowance — Rice List",
    emoji: "🍚",
    art: Soup,
    grad: "from-orange-100 to-red-50",
    ring: "ring-orange-400",
    pitch: "Student-wise Rice ବଣ୍ଟନ ଖାତା — Parent signature column ସହିତ A4 landscape format।",
  },
  {
    key: "fsaCost",
    title: "FSA · Cooking Cost Distribution",
    subtitle: "FSA — Cash Cost ବଣ୍ଟନ ଖାତା",
    emoji: "💰",
    art: ClipboardCheck,
    grad: "from-lime-100 to-emerald-50",
    ring: "ring-emerald-400",
    pitch: "Cash component ବଣ୍ଟନ Sheet — audit ପାଇଁ ready।",
  },
  {
    key: "ogfrUc",
    title: "OGFR UC + Annexure",
    subtitle: "FORM OGFR-23 + OGFR-24 + ANNEXURE-1",
    emoji: "📜",
    art: FileText,
    grad: "from-clay/20 to-orange-50",
    ring: "ring-clay",
    pitch: "Utilization Certificate complete pack — Print-ready PDF, exact required format।",
  },
  {
    key: "ingredient",
    title: "Ingredient Report",
    subtitle: "ସବୁ Ingredient ର Qty + Amount",
    emoji: "🥬",
    art: Carrot,
    grad: "from-green-100 to-lime-50",
    ring: "ring-green-400",
    pitch: "Daily Entry ରୁ auto-derived Ingredient consumption — manual rework ର ଆବଶ୍ୟକତା ନାହିଁ।",
  },
  {
    key: "nutritionLadoo",
    title: "Nutritious Ladoo Report",
    subtitle: "ପୁଷ୍ଟି ଲଡୁ Receipt",
    emoji: "🍡",
    art: Cookie,
    grad: "from-rose-100 to-pink-50",
    ring: "ring-rose-400",
    pitch: "ପୁଷ୍ଟି ଲଡୁ ତିଆରି ପାଇଁ ବ୍ୟବହୃତ Ingredient Table (Sl.No, Date, Particular, Qty, Rate, Amount) — CCH + Headmaster signature block ସହିତ Official Format।",
  },
  {
    key: "ladooMaking",
    title: "Ladoo Making Charge Report",
    subtitle: "ଲଡୁ ପ୍ରସ୍ତୁତି Charge Receipt",
    emoji: "👩‍🍳",
    art: Sun,
    grad: "from-purple-100 to-violet-50",
    ring: "ring-purple-400",
    pitch: "CCH-wise Ladoo ପ୍ରସ୍ତୁତି Charge Table — Quantity (pieces), Rate, Amount column ସହିତ। CCH + Headmaster signature block official format।",
  },
];

// Academic Years to surface — last 3 + current + next 1.
function buildAcademicYears() {
  const d = new Date();
  const m = d.getMonth();
  const y = d.getFullYear();
  const cur = m >= 3 ? y : y - 1;
  const out = [];
  for (let s = cur + 1; s >= cur - 3; s -= 1) {
    const ee = String((s + 1) % 100).padStart(2, "0");
    out.push({ value: `${s}-${ee}`, label: `${s}-${ee}` });
  }
  return out;
}
const AY_OPTIONS = buildAcademicYears();

// Given an Academic Year value like "2025-26", return [2025, 2026].
function ayToCalendarYears(ay) {
  const s = Number((ay || "").split("-")[0]);
  if (!s) return [new Date().getFullYear()];
  return [s, s + 1];
}

// For a calendar year, which months belong to the AY? AY starts April of
// the first year and ends March of the second. So:
//   year === start → months Apr–Dec (4..12)
//   year === start+1 → months Jan–Mar (1..3)
function ayMonthsFor(ay, year) {
  const [s] = ayToCalendarYears(ay);
  if (year === s) return [4, 5, 6, 7, 8, 9, 10, 11, 12];
  if (year === s + 1) return [1, 2, 3];
  return [];
}

const MONTH_NAMES = ["", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

// Map calendar month (1-12) → Quarter key. Used so OGFR can render its
// quarter-scoped layout when a single month is picked.
function monthToQuarter(month) {
  if (month >= 4 && month <= 6) return "Q1";
  if (month >= 7 && month <= 9) return "Q2";
  if (month >= 10 && month <= 12) return "Q3";
  return "Q4";
}

// =================================================================
// MAIN PAGE
// =================================================================
export default function ReportsHub() {
  useRateCardsSync();
  const { active } = useActiveSchool();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialTool = params.get("tool") || "";
  const [tool, setTool] = useState(initialTool);

  useEffect(() => {
    if (tool) setParams({ tool }, { replace: true });
    else setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool]);

  return (
    <div className="min-h-screen bg-sand-50 text-ink-900">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {!tool ? (
          <ToolGallery onPick={setTool} />
        ) : (
          <ToolViewer
            tool={TOOLS.find((t) => t.key === tool)}
            schoolProfile={active}
            onBack={() => setTool("")}
          />
        )}
      </div>
    </div>
  );
}

// =================================================================
// GALLERY VIEW
// =================================================================
function ToolGallery({ onPick }) {
  return (
    <div className="space-y-6" data-testid="reports-hub-gallery">
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-clay">
          <Sparkles className="h-4 w-4" />
          <p className="text-[0.7rem] font-bold uppercase tracking-[0.18em]">Reports Hub · ସମସ୍ତ Report One Place ରେ</p>
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">
          ୩ ଘଣ୍ଟାର କାମ <span className="text-clay">୩ ମିନିଟ୍</span> ରେ —
          <span className="ml-1">Print, PDF, Excel & Word Ready 🚀</span>
        </h1>
        <p className="max-w-3xl text-sm text-ink-700">
          ତଳର ଯେକୌଣସି Tool select କରନ୍ତୁ → Academic Year, Year, Month (Date Optional) ବାଛନ୍ତୁ → <b>Go</b> click କରନ୍ତୁ। ଆପଣଙ୍କ Report ତୁରନ୍ତ ready ହୋଇଯିବ — Standard format ରେ, ଓଡ଼ିଆ + English ଉଭୟ ଭାଷାରେ।
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {TOOLS.map((t) => {
          const Icon = t.art;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => onPick(t.key)}
              data-testid={`reports-tool-card-${t.key}`}
              className={`group relative overflow-hidden rounded-3xl border-2 border-transparent bg-gradient-to-br ${t.grad} p-5 text-left shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:${t.ring} hover:ring-2`}
            >
              <div className="absolute -right-4 -top-4 text-7xl opacity-30 transition group-hover:scale-110 group-hover:opacity-50">
                {t.emoji}
              </div>
              <div className="relative z-10 space-y-3">
                <div className="flex items-center gap-2 text-ink-900">
                  <Icon className="h-5 w-5 text-clay-dark" />
                  <span className="text-2xl">{t.emoji}</span>
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold leading-tight text-ink-900">{t.title}</h3>
                  <p className="mt-0.5 text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-clay-dark">{t.subtitle}</p>
                </div>
                <p className="text-xs leading-5 text-ink-700">{t.pitch}</p>
                <div className="pt-1">
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-clay-dark group-hover:bg-white">
                    Open Report <ArrowLeft className="h-3 w-3 rotate-180" />
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="rounded-2xl border border-dashed border-clay/40 bg-clay/5 p-4 text-xs text-ink-700">
        <p className="flex items-start gap-2">
          <Boxes className="mt-0.5 h-3.5 w-3.5 shrink-0 text-clay" />
          <span>
            <b>Tip:</b> Date field optional — খালি ରଖିଲେ ସମ୍ପୂର୍ଣ୍ଣ Month ର report ମିଳିବ। Specific date ଦେଲେ ସେହି ଦିନର data filter ହୋଇ ଯିବ।
          </span>
        </p>
      </div>
    </div>
  );
}

// =================================================================
// TOOL VIEWER — selectors + report area
// =================================================================
function ToolViewer({ tool, schoolProfile, onBack }) {
  const [ay, setAy] = useState(AY_OPTIONS[1]?.value || AY_OPTIONS[0]?.value);
  const yearChoices = ayToCalendarYears(ay);
  const [year, setYear] = useState(yearChoices[0]);
  const monthChoices = ayMonthsFor(ay, year);
  const [month, setMonth] = useState(monthChoices[0] || 4);
  const [date, setDate] = useState(""); // YYYY-MM-DD or empty

  // Reset month / year when AY changes
  useEffect(() => {
    const ys = ayToCalendarYears(ay);
    if (!ys.includes(year)) setYear(ys[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ay]);
  useEffect(() => {
    const ms = ayMonthsFor(ay, year);
    if (!ms.includes(month)) setMonth(ms[0] || 4);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, ay]);

  const [generated, setGenerated] = useState(null); // payload after Go
  const [loading, setLoading] = useState(false);
  const reportRef = useRef(null);

  // iter148 — Mobile report scaling driver. Scale the A4-landscape
  // print-page down to fit the phone width via a JS-set `--rc-scale`
  // CSS custom property (the pure-CSS clamp/calc approach was rejected
  // by browser parsers because `calc(length / number)` yields <length>,
  // but `scale()` requires <number>). We also lock the canvas's own
  // height to `intrinsicHeight * scale` so the layout box collapses
  // and the page gets no horizontal scroll.
  useEffect(() => {
    const el = reportRef.current;
    if (!el) return undefined;
    let rafId = null;
    const apply = () => {
      rafId = null;
      const w = typeof window !== "undefined" ? window.innerWidth : 1440;
      if (w >= 768) {
        el.style.removeProperty("--rc-scale");
        el.style.height = "";
        return;
      }
      const scale = Math.max(0.3, Math.min(0.95, (w - 16) / 1123));
      el.style.setProperty("--rc-scale", String(scale));
      const first = el.firstElementChild;
      const innerH = first ? first.scrollHeight : 0;
      el.style.height = innerH ? `${Math.ceil(innerH * scale)}px` : "";
    };
    const schedule = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(apply);
    };
    schedule();
    window.addEventListener("resize", schedule);
    // Watch inner report mutations (reports may lazy-grow when tables render).
    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(schedule);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }
    return () => {
      window.removeEventListener("resize", schedule);
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (ro) ro.disconnect();
    };
  }, [generated]);
  // iter147 — auto-refresh reports whenever a Daily Entry is saved.
  // iter148 — same trigger on Vendors/CCH edits (feeds Hand Receipts).
  const dailyEntriesVersion = useDailyEntriesVersion();
  const { vendorsV, cchsV } = useMasterDataVersion();

  const runFetch = async (silent = false) => {
    if (!schoolProfile || !ay || !year || !month) return;
    if (!silent) setLoading(true);
    try {
      const payload = await composeReportPayload({ tool, schoolProfile, ay, year, month, date });
      setGenerated(payload);
      if (!silent) toast.success("Report ready — Print / Export buttons enabled");
      else toast.success("Report refreshed with latest Daily Entries", { duration: 2500 });
    } catch (err) {
      console.error(err);
      if (!silent) toast.error(err?.message || "Could not generate report");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Silent refetch when Daily Entries or Vendors/CCHs change AND we've
  // already generated once. iter147 + iter148.
  useEffect(() => {
    if (!generated) return;
    runFetch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyEntriesVersion, vendorsV, cchsV]);

  const onGo = async () => {
    if (!schoolProfile) {
      toast.error("ପ୍ରଥମେ ଗୋଟିଏ school select କରନ୍ତୁ");
      return;
    }
    if (!ay || !year || !month) {
      toast.error("Academic Year, Year & Month required");
      return;
    }
    await runFetch(false);
  };

  return (
    <div className="space-y-4" data-testid={`reports-tool-viewer-${tool.key}`}>
      <header className="flex flex-wrap items-center gap-3">
        <Button size="sm" variant="ghost" onClick={onBack} data-testid="reports-back-btn" className="gap-1">
          <ArrowLeft className="h-3.5 w-3.5" /> All Reports
        </Button>
        <div className="flex items-center gap-2">
          <span className="text-2xl">{tool.emoji}</span>
          <h2 className="font-heading text-xl font-bold sm:text-2xl">{tool.title}</h2>
        </div>
        <p className="basis-full text-xs text-ink-700 sm:basis-auto">{tool.subtitle}</p>
      </header>

      {/* Selectors */}
      <section className="rounded-2xl border border-border bg-white p-4 shadow-sm" data-testid="reports-selectors">
        <p className="mb-3 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-clay-dark">
          Select Period · ଅବଧି ବାଛନ୍ତୁ
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <label className="text-xs">
            <span className="block font-semibold text-ink-700">Academic Year</span>
            <Select value={ay} onValueChange={setAy}>
              <SelectTrigger className="mt-1 h-9" data-testid="reports-select-ay"><SelectValue /></SelectTrigger>
              <SelectContent>
                {AY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="text-xs">
            <span className="block font-semibold text-ink-700">Year</span>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="mt-1 h-9" data-testid="reports-select-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {yearChoices.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="text-xs">
            <span className="block font-semibold text-ink-700">Month</span>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="mt-1 h-9" data-testid="reports-select-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                {monthChoices.map((m) => <SelectItem key={m} value={String(m)}>{MONTH_NAMES[m]}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="text-xs">
            <span className="block font-semibold text-ink-700">Date (optional)</span>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1 h-9"
              data-testid="reports-select-date"
              min={`${year}-${String(month).padStart(2, "0")}-01`}
              max={`${year}-${String(month).padStart(2, "0")}-31`}
            />
          </label>
          <div className="flex items-end">
            <Button
              size="sm"
              onClick={onGo}
              disabled={loading}
              data-testid="reports-go-btn"
              className="h-9 w-full gap-1.5 bg-clay text-white hover:bg-clay-dark"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {loading ? "Generating…" : "Go · ଚାଲାନ୍ତୁ"}
            </Button>
          </div>
        </div>
        {date && (
          <p className="mt-2 text-[0.7rem] text-ink-700">
            <b>Filter:</b> Only data for <b>{date}</b> will be included. Clear the date to see the full month.
          </p>
        )}
      </section>

      {/* Toolbar + report */}
      {generated && (
        <ReportToolbarAndCanvas
          reportRef={reportRef}
          tool={tool}
          generated={generated}
        />
      )}

      {!generated && !loading && (
        <div className="rounded-2xl border-2 border-dashed border-clay/30 bg-white p-12 text-center" data-testid="reports-empty-hint">
          <p className="text-3xl">{tool.emoji}</p>
          <p className="mt-3 font-heading text-lg font-semibold">Click <b>Go</b> ବଟନ୍ click କରନ୍ତୁ</p>
          <p className="mt-1 text-xs text-ink-700">Period select କରି Generate କରନ୍ତୁ — report ଏଠାରେ ଦେଖାଯିବ।</p>
        </div>
      )}
    </div>
  );
}

// =================================================================
// TOOLBAR + REPORT CANVAS
// =================================================================
function ReportToolbarAndCanvas({ reportRef, tool, generated }) {
  const [busy, setBusy] = useState(false);

  const doPrint = () => printReport();

  const doPdf = async () => {
    setBusy(true);
    try {
      await downloadPdf(reportRef, generated.schoolInfo, tool.key);
      toast.success("PDF downloaded");
    } catch (err) {
      console.error(err);
      toast.error("PDF export failed");
    } finally { setBusy(false); }
  };

  const doExport = (kind) => {
    if (!reportRef.current) return;
    const reportHtml = reportRef.current.innerHTML;
    const wrapped = wrapForOffice(reportHtml, generated.schoolInfo, tool.title, kind);
    const ext = kind === "word" ? "doc" : "xls";
    const mime = kind === "word"
      ? "application/msword;charset=utf-8"
      : "application/vnd.ms-excel;charset=utf-8";
    const blob = new Blob([wrapped], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeFileName(generated.schoolInfo.schoolName || "School")}_${tool.key}_${generated.schoolInfo.financialYear}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`${kind === "word" ? "Word" : "Excel"} downloaded`);
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-white p-3 shadow-sm no-print" data-testid="reports-toolbar">
        <p className="mr-auto text-xs text-ink-700">
          <b>{generated.label}</b> · {generated.schoolInfo.schoolName} · UDISE {generated.schoolInfo.udiseCode}
        </p>
        <Button size="sm" variant="outline" onClick={doPrint} disabled={busy} data-testid="reports-print-btn" className="gap-1.5">
          <Printer className="h-3.5 w-3.5" /> Print
        </Button>
        <Button size="sm" variant="outline" onClick={doPdf} disabled={busy} data-testid="reports-pdf-btn" className="gap-1.5 border-rose-400 text-rose-700 hover:bg-rose-50">
          <FileDown className="h-3.5 w-3.5" /> PDF
        </Button>
        <Button size="sm" variant="outline" onClick={() => doExport("excel")} disabled={busy} data-testid="reports-excel-btn" className="gap-1.5 border-emerald-400 text-emerald-700 hover:bg-emerald-50">
          <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
        </Button>
        <Button size="sm" variant="outline" onClick={() => doExport("word")} disabled={busy} data-testid="reports-word-btn" className="gap-1.5 border-blue-400 text-blue-700 hover:bg-blue-50">
          <FileText className="h-3.5 w-3.5" /> Word
        </Button>
      </div>

      {/* iter148 — Report canvas. On mobile (<768px) we auto-scale the
          intrinsic ~1123px A4-landscape content down to fit the viewport
          via `.report-canvas` CSS (transform:scale on the inner print-page).
          Users can pinch-zoom in via `touch-action: pan-x pan-y pinch-zoom`.
          A compact "Pinch to zoom" hint sits above the canvas. */}
      <div className="mt-3 flex items-center justify-between rounded-lg bg-sand-100 px-3 py-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ink-700 md:hidden no-print" data-testid="mobile-report-hint">
        <span>↔ Pinch / swipe to zoom &amp; pan</span>
        <span className="text-clay-dark">A4 landscape preview</span>
      </div>
      <div ref={reportRef} className="report-canvas mt-3 bg-white" data-testid="reports-canvas">
        <ReportRenderer tool={tool} generated={generated} />
      </div>
    </>
  );
}

function ReportRenderer({ tool, generated }) {
  const { schoolInfo, totals, odiaReceiptGroups, annexureEntries, ingredientWiseReport, dryRationData, ucScope } = generated;
  switch (tool.key) {
    case "handReceipts":
      // ALL receipts surface here: quarterly hand-receipt summary at top,
      // then EVERY category receipt (Grocery, Vegetable, Egg, Fuel, Nutrition
      // Ladoo, Ladoo Making, CCH wages) one after another. Earlier we hid
      // the nutrition/ladoo categories from this view — that's been removed.
      return (
        <>
          <HandReceiptReport schoolInfo={schoolInfo} totals={totals} />
          <OdiaReceiptsReport schoolInfo={schoolInfo} odiaReceiptGroups={odiaReceiptGroups} />
        </>
      );
    case "dryRation":
      return <DryRationReport schoolInfo={schoolInfo} dryRationData={dryRationData} />;
    case "fsaRice":
      return <FsaRiceDistribution schoolInfo={schoolInfo} dryRationData={dryRationData} />;
    case "fsaCost":
      return <FsaCostDistribution schoolInfo={schoolInfo} dryRationData={dryRationData} />;
    case "ogfrUc":
      return (
        <>
          <Ogfr23Report schoolInfo={schoolInfo} totals={totals} ucScope={ucScope} />
          <Ogfr24Report schoolInfo={schoolInfo} totals={totals} ucScope={ucScope} annexureEntries={annexureEntries} />
          <AnnexureReport schoolInfo={schoolInfo} annexureEntries={annexureEntries} />
        </>
      );
    case "ingredient":
      return <IngredientReport schoolInfo={schoolInfo} ingredientWiseReport={ingredientWiseReport} totals={totals} />;
    case "nutritionLadoo":
      // User-requested (iter 78 → 80 → 81): landscape day-wise overview +
      // per-class day-wise pages. The overview page shows the school-level
      // aggregate; the 4 per-class pages show date-by-date ingredient
      // consumption ONLY for that class, each with its own SMC +
      // Headmaster signature block. PDF export will paginate them naturally.
      return (
        <>
          <NutritiousLadooDayTable
            schoolInfo={schoolInfo}
            dailyLadoo={generated.dailyLadoo}
          />
          <PerClassLadooPages
            schoolInfo={schoolInfo}
            dailyLadoo={generated.dailyLadoo}
          />
        </>
      );
    case "ladooMaking":
      // User-requested (iter 78): landscape — day-wise prepared/charge/total
      // table, then CCH-wise split table with individual signatures, plus
      // SMC + Headmaster sigs at the bottom. Fits one page.
      return (
        <LadooMakingDayAndCchTable
          schoolInfo={schoolInfo}
          dailyLadoo={generated.dailyLadoo}
          cchList={generated.cchList}
        />
      );
    default:
      return null;
  }
}

// =================================================================
// LADOO INGREDIENT TABLE
// =================================================================
// Replaces the receipt-card style for both Nutritious Ladoo + Ladoo Making
// reports. Renders a single official-style table (Sl.No / Date / Particular
// / Unit / Quantity / Rate / Amount), a Grand Total row, and a two-column
// signature block (CCH Signature ← → Headmaster Signature) at the bottom.
// Stays inside <ReportShell> via the wrapping <div class="print-page"> so it
// participates in Print + PDF capture exactly like the other reports.
// =================================================================
// DAY-WISE LADOO BREAKDOWN BUILDER
// =================================================================
// Walks the daily entries for the picked period, picks the days that are
// ladoo days (Tue / Thu / Sat per the meal-plan calendar), and returns a
// rich per-day row for the Nutritious Ladoo + Ladoo Making reports.
//
// Per-row shape:
//   { date, dayName, mealName, served (= primary+upper),
//     ingredients: [{ name, qty, unit, rate, amount }],
//     ladooPieces, makingCharge, totalAmount (= sum of all ingredients) }
//
// `ladooPieces` = served count (1 ladoo / student). `makingCharge` = pieces
// × Rs 0.26 — the CCH service charge component.
function buildDailyLadooBreakdown(dailyEntries) {
  const LADOO_RATE = 0.26;
  const rows = [];
  (dailyEntries || []).forEach((e) => {
    if (!e || e.entryType === "dryration" || !isMealDay(e)) return;
    const breakdown = buildDayBreakdown(e.date, e.attendance || {});
    if (!breakdown || !breakdown.ladoo) return; // not a ladoo day
    const served = (breakdown.primaryGroupCount || 0) + (breakdown.upperGroupCount || 0);
    if (served <= 0) return;
    const ingredients = (breakdown.aggregate || [])
      .filter((row) => row.isLadoo && row.name !== "Ladoo Making Cost")
      .map((row) => {
        // Look up the meal-plan rate so we can show "Rs 150/kg" style rates
        // even though aggregate only carries totalAmount + unit.
        const def = LADOO_LINES.find((l) => l.item === row.name);
        return {
          name: row.name,
          qty: Number(row.totalQty || 0),
          unit: row.unit || (def ? def.unit : ""),
          rate: def ? def.rate : 0,
          rateLabel: def ? def.rateLabel : "",
          amount: Number(row.totalAmount || 0),
        };
      });
    const ingredientAmt = ingredients.reduce((s, ing) => s + ing.amount, 0);
    const dayObj = new Date(e.date + "T00:00:00");
    const dayName = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][dayObj.getDay()];
    // Per-class breakdown so the report can split the "Served" header into
    // Bal Vatika / Primary / Upper Primary / High School sub-columns (iter 79).
    const perClass = (breakdown.perClass || []).map((c) => ({
      key: c.key, label: c.label, count: Number(c.count || 0), ladooCost: Number(c.ladooCost || 0),
    }));
    rows.push({
      date: e.date,
      dayName,
      mealName: breakdown.mealName || "Ladoo Day",
      served,
      perClass,
      ingredients,
      ladooPieces: served, // 1 ladoo per served student
      makingCharge: Number((served * LADOO_RATE).toFixed(2)),
      totalAmount: Number(ingredientAmt.toFixed(2)),
      makingRate: LADOO_RATE,
    });
  });
  rows.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  return rows;
}

// =================================================================
// NUTRITIOUS LADOO — DAY-WISE LANDSCAPE TABLE
// =================================================================
// Date / Day / Served Ladoo / one column per ladoo ingredient (Qty) /
// Day Expenditure. Grand-total row + SMC Chairperson + Headmaster sigs.
function NutritiousLadooDayTable({ schoolInfo, dailyLadoo }) {
  // Build a consistent ordered ingredient list from LADOO_LINES (excluding
  // the "Ladoo Making Cost" line — that belongs in the Making Charge report)
  const cols = LADOO_LINES.filter((l) => l.item !== "Ladoo Making Cost");
  // Stable ordered class definitions — used for the header sub-columns AND
  // the class-wise summary table at the bottom. Matches buildDayBreakdown.
  const CLASS_DEFS = [
    { key: "balVatika", label: "Bal Vatika" },
    { key: "primary", label: "Primary" },
    { key: "upperPrimary", label: "Upper Primary" },
    { key: "highSchool", label: "High School" },
  ];

  // Day rows + per-column totals
  const colTotals = cols.map(() => ({ qty: 0, amount: 0 }));
  let totalServed = 0;
  let totalAmount = 0;
  // Per-class totals for the summary block — sum of served counts + ladoo
  // expenditure (Rs 3.50 / served student per `breakdown.perClass.ladooCost`).
  const classTotals = CLASS_DEFS.map(() => ({ served: 0, ladooCost: 0 }));
  dailyLadoo.forEach((d) => {
    totalServed += d.served;
    totalAmount += d.totalAmount;
    cols.forEach((c, i) => {
      const ing = d.ingredients.find((x) => x.name === c.item);
      if (ing) {
        colTotals[i].qty += ing.qty;
        colTotals[i].amount += ing.amount;
      }
    });
    CLASS_DEFS.forEach((cd, i) => {
      const pc = (d.perClass || []).find((p) => p.key === cd.key) || { count: 0, ladooCost: 0 };
      classTotals[i].served += pc.count;
      classTotals[i].ladooCost += pc.ladooCost;
    });
  });

  return (
    <div className="print-page landscape" data-testid="nutritious-ladoo-day-table">
      <header className="text-center">
        <h1 className="font-heading text-xl font-bold uppercase tracking-wide text-ink-900">
          ପୁଷ୍ଟି ଲଡୁ CLASS-WISE &amp; DAY-WISE INGREDIENT REPORT
        </h1>
        <p className="mt-0.5 text-xs font-semibold text-ink-700">
          Nutritious Ladoo — Class-wise Day-wise Ingredient &amp; Expenditure Statement
        </p>
        <div className="mx-auto mt-2 grid max-w-5xl grid-cols-2 gap-y-0.5 text-[10pt]">
          <div className="text-left"><b>School:</b> {schoolInfo.schoolName}</div>
          <div className="text-right"><b>UDISE:</b> {schoolInfo.udiseCode}</div>
          <div className="text-left"><b>District:</b> {schoolInfo.district} · <b>Block:</b> {schoolInfo.block}</div>
          <div className="text-right"><b>Financial Year:</b> {schoolInfo.financialYear}</div>
        </div>
      </header>

      {dailyLadoo.length === 0 ? (
        <p className="mt-6 rounded-md border border-dashed border-ink-300 bg-sand-50 p-6 text-center text-xs text-ink-700">
          ଏହି ଅବଧି ପାଇଁ ପୁଷ୍ଟି ଲଡୁ ତଥ୍ୟ ଉପଲବ୍ଧ ନୁହେଁ। (No ladoo-day daily entries found for this period.)
        </p>
      ) : (
        <>
          <table className="report-table mt-3 w-full">
            <thead>
              <tr>
                <th rowSpan={2} className="text-center" style={{ width: "3%" }}>Sl</th>
                <th rowSpan={2} className="text-center" style={{ width: "7%" }}>Date</th>
                <th rowSpan={2} className="text-center" style={{ width: "6%" }}>Day</th>
                {/* Class-wise served counts — replaces the single Served column */}
                <th colSpan={CLASS_DEFS.length + 1} className="text-center">Served (No. of Ladoo) — Class-wise</th>
                <th colSpan={cols.length} className="text-center">Ingredient-wise Quantity (g/ml)</th>
                <th rowSpan={2} className="text-center" style={{ width: "8%" }}>Day Expenditure (₹)</th>
              </tr>
              <tr>
                {CLASS_DEFS.map((c) => (
                  <th key={c.key} className="text-center text-[8.5pt]">{c.label}</th>
                ))}
                <th className="text-center text-[8.5pt]" style={{ background: "#e8e1cf" }}>Total</th>
                {cols.map((c) => (
                  <th key={c.item} className="text-center text-[8.5pt]">
                    {c.item}<br />
                    <span className="text-[7.5pt] font-normal text-ink-700">{c.rateLabel}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dailyLadoo.map((d, i) => (
                <tr key={d.date}>
                  <td className="text-center">{i + 1}</td>
                  <td className="text-center">{d.date}</td>
                  <td className="text-center">{d.dayName}</td>
                  {CLASS_DEFS.map((cd) => {
                    const pc = (d.perClass || []).find((p) => p.key === cd.key) || { count: 0 };
                    return <td key={cd.key} className="text-center tabular-nums">{pc.count || "—"}</td>;
                  })}
                  <td className="text-center tabular-nums" style={{ background: "#fbf6e8", fontWeight: 600 }}>{d.served}</td>
                  {cols.map((c) => {
                    const ing = d.ingredients.find((x) => x.name === c.item);
                    return (
                      <td key={c.item} className="text-right tabular-nums">
                        {ing ? ing.qty.toLocaleString("en-IN", { maximumFractionDigits: 1 }) : "—"}
                      </td>
                    );
                  })}
                  <td className="text-right tabular-nums">{money(d.totalAmount)}</td>
                </tr>
              ))}
              <tr style={{ background: "#f3eee2", fontWeight: 700 }}>
                <td colSpan={3} className="text-right uppercase">Grand Total</td>
                {classTotals.map((t, i) => (
                  <td key={CLASS_DEFS[i].key} className="text-center tabular-nums">{t.served}</td>
                ))}
                <td className="text-center tabular-nums" style={{ background: "#e8e1cf" }}>{totalServed}</td>
                {colTotals.map((t, i) => (
                  <td key={cols[i].item} className="text-right tabular-nums">
                    {t.qty.toLocaleString("en-IN", { maximumFractionDigits: 1 })}
                  </td>
                ))}
                <td className="text-right tabular-nums">{money(totalAmount)}</td>
              </tr>
            </tbody>
          </table>

          {/* Class-wise INGREDIENT summary: one row per class showing total
              served + per-ingredient quantity (qty/student × class served) +
              total ladoo expenditure. Answers "how much Ragi did Primary
              consume this month?" at a glance. Replaces the simpler
              served-only summary that shipped in iter 79. */}
          <h2 className="mt-3 font-semibold text-ink-900">Class-wise Ingredient Summary (Period Totals)</h2>
          <table className="report-table mt-1 w-full">
            <thead>
              <tr>
                <th rowSpan={2} className="text-center" style={{ width: "3%" }}>Sl</th>
                <th rowSpan={2} className="text-left" style={{ width: "12%" }}>Class</th>
                <th rowSpan={2} className="text-center" style={{ width: "8%" }}>Total Served</th>
                <th colSpan={cols.length} className="text-center">Ingredient Quantity (g / ml)</th>
                <th rowSpan={2} className="text-center" style={{ width: "10%" }}>Total Expenditure (₹)</th>
              </tr>
              <tr>
                {cols.map((c) => (
                  <th key={c.item} className="text-center text-[8.5pt]">
                    {c.item}<br />
                    <span className="text-[7.5pt] font-normal text-ink-700">{c.rateLabel}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CLASS_DEFS.map((c, i) => {
                const served = classTotals[i].served;
                return (
                  <tr key={c.key}>
                    <td className="text-center">{i + 1}</td>
                    <td>{c.label}</td>
                    <td className="text-right tabular-nums">{served}</td>
                    {cols.map((col) => {
                      // Per-student qty from the meal-plan definition × served
                      // count for this class over the period. Ladoo ingredient
                      // qty per student is identical across all classes so this
                      // is the cleanest derivation — no need to plumb per-class
                      // qty through buildDayBreakdown.
                      const perStudent = Number(col.qty || 0);
                      const totalQty = perStudent * served;
                      return (
                        <td key={col.item} className="text-right tabular-nums">
                          {totalQty.toLocaleString("en-IN", { maximumFractionDigits: 1 })}
                        </td>
                      );
                    })}
                    <td className="text-right tabular-nums">{money(classTotals[i].ladooCost)}</td>
                  </tr>
                );
              })}
              <tr style={{ background: "#f3eee2", fontWeight: 700 }}>
                <td colSpan={2} className="text-right uppercase">Grand Total</td>
                <td className="text-right tabular-nums">{totalServed}</td>
                {cols.map((col) => {
                  const totalQty = Number(col.qty || 0) * totalServed;
                  return (
                    <td key={col.item} className="text-right tabular-nums">
                      {totalQty.toLocaleString("en-IN", { maximumFractionDigits: 1 })}
                    </td>
                  );
                })}
                <td className="text-right tabular-nums">{money(classTotals.reduce((s, t) => s + t.ladooCost, 0))}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      <p className="mt-2 text-[8.5pt] text-ink-700">
        <b>Rate Reference:</b>{" "}
        {cols.map((c, i) => (
          <span key={c.item}>{c.item} — {c.rateLabel}{i < cols.length - 1 ? "; " : ""}</span>
        ))}
      </p>

      {/* SMC Chairperson + Headmaster sigs */}
      <div className="mt-6 grid grid-cols-2 gap-12 text-xs">
        <div className="text-center">
          <p className="border-t border-ink-900 pt-1 font-semibold">SMC Chairperson Signature / ବିଦ୍ୟାଳୟ ପରିଚାଳନା ସମିତିର ସଭାପତି</p>
          <p className="mt-0.5 text-[9pt] text-ink-700">Name: ________________________</p>
          <p className="text-[9pt] text-ink-700">Date: ____________________</p>
        </div>
        <div className="text-center">
          <p className="border-t border-ink-900 pt-1 font-semibold">Headmaster Signature / ପ୍ରଧାନ ଶିକ୍ଷକ ସ୍ୱାକ୍ଷର</p>
          <p className="mt-0.5 text-[9pt] text-ink-700">{schoolInfo.schoolName}</p>
          <p className="text-[9pt] text-ink-700">Date: ____________________</p>
        </div>
      </div>
    </div>
  );
}

// =================================================================
// PER-CLASS LADOO PAGES (Nutritious Ladoo)
// =================================================================
// Renders 4 standalone landscape A4 pages — one per class (Bal Vatika /
// Primary / Upper Primary / High School). Each page shows date-by-date
// ingredient quantity consumption ONLY for that class + its own SMC +
// Headmaster signature block. Per-class qty is derived from the meal-plan
// per-student qty multiplied by the class served count for that day —
// per-student qty is identical across classes on ladoo days, so this is
// the cleanest derivation without extra plumbing.
function PerClassLadooPages({ schoolInfo, dailyLadoo }) {
  const cols = LADOO_LINES.filter((l) => l.item !== "Ladoo Making Cost");
  const CLASS_DEFS = [
    { key: "balVatika", label: "Bal Vatika", labelOdia: "ବାଳ ବାଟିକା" },
    { key: "primary", label: "Primary", labelOdia: "ପ୍ରାଥମିକ" },
    { key: "upperPrimary", label: "Upper Primary", labelOdia: "ଉଚ୍ଚ ପ୍ରାଥମିକ" },
    { key: "highSchool", label: "High School", labelOdia: "ଉଚ୍ଚ ବିଦ୍ୟାଳୟ" },
  ];

  return (
    <>
      {CLASS_DEFS.map((cd) => {
        // Day rows for THIS class. We render the page even when the class
        // had zero ladoos in the period — every class gets its own
        // signature page, important for audit completeness.
        const dayRows = dailyLadoo
          .map((d) => {
            const pc = (d.perClass || []).find((p) => p.key === cd.key) || { count: 0, ladooCost: 0 };
            return { ...d, classCount: pc.count, classLadooCost: pc.ladooCost };
          })
          .filter((d) => d.classCount > 0);

        const colTotals = cols.map(() => 0);
        let totalServed = 0;
        let totalAmount = 0;
        dayRows.forEach((d) => {
          totalServed += d.classCount;
          totalAmount += d.classLadooCost;
          cols.forEach((c, i) => {
            colTotals[i] += Number(c.qty || 0) * d.classCount;
          });
        });

        return (
          <div
            key={cd.key}
            className="print-page landscape"
            data-testid={`nutritious-ladoo-class-page-${cd.key}`}
          >
            <header className="text-center">
              <h1 className="font-heading text-xl font-bold uppercase tracking-wide text-ink-900">
                ପୁଷ୍ଟି ଲଡୁ INGREDIENT REPORT — {cd.labelOdia} / {cd.label}
              </h1>
              <p className="mt-0.5 text-xs font-semibold text-ink-700">
                Nutritious Ladoo — Day-wise Ingredient Statement (Class: {cd.label})
              </p>
              <div className="mx-auto mt-2 grid max-w-5xl grid-cols-2 gap-y-0.5 text-[10pt]">
                <div className="text-left"><b>School:</b> {schoolInfo.schoolName}</div>
                <div className="text-right"><b>UDISE:</b> {schoolInfo.udiseCode}</div>
                <div className="text-left"><b>District:</b> {schoolInfo.district} · <b>Block:</b> {schoolInfo.block}</div>
                <div className="text-right"><b>Financial Year:</b> {schoolInfo.financialYear}</div>
              </div>
            </header>

            {dayRows.length === 0 ? (
              <p className="mt-6 rounded-md border border-dashed border-ink-300 bg-sand-50 p-6 text-center text-xs text-ink-700">
                ଏହି ଅବଧି ପାଇଁ <b>{cd.labelOdia} ({cd.label})</b> ଶ୍ରେଣୀରେ କୌଣସି ଲଡୁ ଦିନ ଡାଟା ଉପଲବ୍ଧ ନୁହେଁ।
              </p>
            ) : (
              <table className="report-table mt-3 w-full">
                <thead>
                  <tr>
                    <th rowSpan={2} className="text-center" style={{ width: "4%" }}>Sl</th>
                    <th rowSpan={2} className="text-center" style={{ width: "9%" }}>Date</th>
                    <th rowSpan={2} className="text-center" style={{ width: "7%" }}>Day</th>
                    <th rowSpan={2} className="text-center" style={{ width: "8%" }}>{cd.label} Served</th>
                    <th colSpan={cols.length} className="text-center">Ingredient Quantity (g / ml)</th>
                    <th rowSpan={2} className="text-center" style={{ width: "10%" }}>Day Expenditure (₹)</th>
                  </tr>
                  <tr>
                    {cols.map((c) => (
                      <th key={c.item} className="text-center text-[8.5pt]">
                        {c.item}<br />
                        <span className="text-[7.5pt] font-normal text-ink-700">{c.rateLabel}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dayRows.map((d, i) => (
                    <tr key={d.date}>
                      <td className="text-center">{i + 1}</td>
                      <td className="text-center">{d.date}</td>
                      <td className="text-center">{d.dayName}</td>
                      <td className="text-center tabular-nums">{d.classCount}</td>
                      {cols.map((c) => {
                        const qty = Number(c.qty || 0) * d.classCount;
                        return (
                          <td key={c.item} className="text-right tabular-nums">
                            {qty.toLocaleString("en-IN", { maximumFractionDigits: 1 })}
                          </td>
                        );
                      })}
                      <td className="text-right tabular-nums">{money(d.classLadooCost)}</td>
                    </tr>
                  ))}
                  <tr style={{ background: "#f3eee2", fontWeight: 700 }}>
                    <td colSpan={3} className="text-right uppercase">Grand Total</td>
                    <td className="text-center tabular-nums">{totalServed}</td>
                    {colTotals.map((q, i) => (
                      <td key={cols[i].item} className="text-right tabular-nums">
                        {q.toLocaleString("en-IN", { maximumFractionDigits: 1 })}
                      </td>
                    ))}
                    <td className="text-right tabular-nums">{money(totalAmount)}</td>
                  </tr>
                </tbody>
              </table>
            )}

            <p className="mt-2 text-[8.5pt] text-ink-700">
              <b>Rate Reference:</b>{" "}
              {cols.map((c, i) => (
                <span key={c.item}>{c.item} — {c.rateLabel}{i < cols.length - 1 ? "; " : ""}</span>
              ))}
            </p>

            <div className="mt-6 grid grid-cols-2 gap-12 text-xs">
              <div className="text-center">
                <p className="border-t border-ink-900 pt-1 font-semibold">SMC Chairperson Signature / ବିଦ୍ୟାଳୟ ପରିଚାଳନା ସମିତିର ସଭାପତି</p>
                <p className="mt-0.5 text-[9pt] text-ink-700">Name: ________________________</p>
                <p className="text-[9pt] text-ink-700">Date: ____________________</p>
              </div>
              <div className="text-center">
                <p className="border-t border-ink-900 pt-1 font-semibold">Headmaster Signature / ପ୍ରଧାନ ଶିକ୍ଷକ ସ୍ୱାକ୍ଷର</p>
                <p className="mt-0.5 text-[9pt] text-ink-700">{schoolInfo.schoolName}</p>
                <p className="text-[9pt] text-ink-700">Date: ____________________</p>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}


// =================================================================
// LADOO MAKING CHARGE — DAY-WISE + CCH-WISE LANDSCAPE TABLE
// =================================================================
// Fits one A4 landscape page: day-wise table (Date / Day / Pieces / Rate /
// Total) → CCH-wise allocation table (CCH Name / Father / Pieces / Charge
// / Signature). Bottom: SMC Chairperson + Headmaster signatures.
function LadooMakingDayAndCchTable({ schoolInfo, dailyLadoo, cchList }) {
  const LADOO_RATE = 0.26;
  const totalPieces = dailyLadoo.reduce((s, d) => s + d.ladooPieces, 0);
  const totalCharge = Number((totalPieces * LADOO_RATE).toFixed(2));

  // Allocate total pieces across CCHs as equally as possible. Last CCH
  // absorbs the rounding remainder so the per-CCH amounts reconcile to
  // exactly `totalCharge`. Falls back to a single "CCH" placeholder when
  // the master roster is empty so the report still renders something useful.
  const cchs = (cchList && cchList.length > 0) ? cchList : [{ name: "CCH", id: "cch-default" }];
  const n = cchs.length;
  const base = Math.floor(totalPieces / n);
  const remainder = totalPieces - base * n;
  const cchRows = cchs.map((c, i) => {
    const pieces = base + (i === n - 1 ? remainder : 0);
    return {
      name: c.name || c.fullName || `CCH ${i + 1}`,
      nameOdia: c.nameOdia || "",
      father: c.father || c.fatherName || "",
      village: c.village || "",
      pieces,
      amount: Number((pieces * LADOO_RATE).toFixed(2)),
    };
  });

  return (
    <div className="print-page landscape" data-testid="ladoo-making-day-cch-table">
      <header className="text-center">
        <h1 className="font-heading text-xl font-bold uppercase tracking-wide text-ink-900">
          ଲଡୁ ପ୍ରସ୍ତୁତି CHARGE REPORT
        </h1>
        <p className="mt-0.5 text-xs font-semibold text-ink-700">
          Ladoo Making Charge — Day-wise &amp; CCH-wise Distribution Statement
        </p>
        <div className="mx-auto mt-2 grid max-w-5xl grid-cols-2 gap-y-0.5 text-[10pt]">
          <div className="text-left"><b>School:</b> {schoolInfo.schoolName}</div>
          <div className="text-right"><b>UDISE:</b> {schoolInfo.udiseCode}</div>
          <div className="text-left"><b>District:</b> {schoolInfo.district} · <b>Block:</b> {schoolInfo.block}</div>
          <div className="text-right"><b>Financial Year:</b> {schoolInfo.financialYear}</div>
        </div>
      </header>

      {/* Day-wise table */}
      <h2 className="mt-3 font-semibold text-ink-900">Day-wise Ladoo Preparation</h2>
      {dailyLadoo.length === 0 ? (
        <p className="mt-2 rounded-md border border-dashed border-ink-300 bg-sand-50 p-3 text-center text-xs text-ink-700">
          ଏହି ଅବଧି ପାଇଁ ଲଡୁ ପ୍ରସ୍ତୁତି ତଥ୍ୟ ଉପଲବ୍ଧ ନୁହେଁ।
        </p>
      ) : (
        <table className="report-table mt-1 w-full">
          <thead>
            <tr>
              <th className="text-center" style={{ width: "6%" }}>Sl</th>
              <th className="text-center" style={{ width: "16%" }}>Date</th>
              <th className="text-center" style={{ width: "12%" }}>Day</th>
              <th className="text-center" style={{ width: "20%" }}>Prepared No. of Ladoo</th>
              <th className="text-center" style={{ width: "20%" }}>Making Charge / Ladoo (₹)</th>
              <th className="text-center">Total Price (₹)</th>
            </tr>
          </thead>
          <tbody>
            {dailyLadoo.map((d, i) => (
              <tr key={d.date}>
                <td className="text-center">{i + 1}</td>
                <td className="text-center">{d.date}</td>
                <td className="text-center">{d.dayName}</td>
                <td className="text-right tabular-nums">{d.ladooPieces}</td>
                <td className="text-right tabular-nums">{LADOO_RATE.toFixed(2)}</td>
                <td className="text-right tabular-nums">{money(d.makingCharge)}</td>
              </tr>
            ))}
            <tr style={{ background: "#f3eee2", fontWeight: 700 }}>
              <td colSpan={3} className="text-right uppercase">Grand Total</td>
              <td className="text-right tabular-nums">{totalPieces}</td>
              <td></td>
              <td className="text-right tabular-nums">{money(totalCharge)}</td>
            </tr>
          </tbody>
        </table>
      )}

      {/* CCH-wise split */}
      <h2 className="mt-4 font-semibold text-ink-900">CCH-wise Allocation &amp; Individual Signatures</h2>
      <table className="report-table mt-1 w-full">
        <thead>
          <tr>
            <th className="text-center" style={{ width: "5%" }}>Sl</th>
            <th className="text-left" style={{ width: "22%" }}>CCH Name</th>
            <th className="text-left" style={{ width: "18%" }}>Father's / Husband's Name</th>
            <th className="text-left" style={{ width: "14%" }}>Village</th>
            <th className="text-center" style={{ width: "11%" }}>Prepared Ladoos</th>
            <th className="text-center" style={{ width: "10%" }}>Total Charge (₹)</th>
            <th className="text-center">Individual Signature</th>
          </tr>
        </thead>
        <tbody>
          {cchRows.map((r, i) => (
            <tr key={i}>
              <td className="text-center">{i + 1}</td>
              <td>{r.name}{r.nameOdia ? <><br /><span className="text-[8.5pt] text-ink-700">{r.nameOdia}</span></> : null}</td>
              <td>{r.father ? <>{r.father} <span className="text-[8pt] text-ink-700">({r.relation === "husband" ? "ସ୍ୱାମୀ" : "ପିତା"})</span></> : "—"}</td>
              <td>{r.village || "—"}</td>
              <td className="text-right tabular-nums">{r.pieces}</td>
              <td className="text-right tabular-nums">{money(r.amount)}</td>
              <td className="text-center text-ink-400">_________________</td>
            </tr>
          ))}
          <tr style={{ background: "#f3eee2", fontWeight: 700 }}>
            <td colSpan={4} className="text-right uppercase">Grand Total</td>
            <td className="text-right tabular-nums">{totalPieces}</td>
            <td className="text-right tabular-nums">{money(totalCharge)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>

      {/* SMC + Headmaster bottom signatures */}
      <div className="mt-6 grid grid-cols-2 gap-12 text-xs">
        <div className="text-center">
          <p className="border-t border-ink-900 pt-1 font-semibold">SMC Chairperson Signature / ବିଦ୍ୟାଳୟ ପରିଚାଳନା ସମିତିର ସଭାପତି</p>
          <p className="mt-0.5 text-[9pt] text-ink-700">Name: ________________________</p>
          <p className="text-[9pt] text-ink-700">Date: ____________________</p>
        </div>
        <div className="text-center">
          <p className="border-t border-ink-900 pt-1 font-semibold">Headmaster Signature / ପ୍ରଧାନ ଶିକ୍ଷକ ସ୍ୱାକ୍ଷର</p>
          <p className="mt-0.5 text-[9pt] text-ink-700">{schoolInfo.schoolName}</p>
          <p className="text-[9pt] text-ink-700">Date: ____________________</p>
        </div>
      </div>
    </div>
  );
}

// =================================================================
// DATA COMPOSITION — reuses the same builders Generator.jsx uses.
// Period semantics:
//   • A single month object is created from the picked (year, month).
//   • dailyEntries are fetched for fromDate..toDate of that month, or
//     just the picked date when set.
//   • OGFR uses month-scope (label "Month YYYY") via ucScope.
// =================================================================
async function composeReportPayload({ tool, schoolProfile, ay, year, month, date }) {
  const sid = schoolProfile?.id;
  if (!sid) throw new Error("No active school");

  // Period boundaries
  const mm = String(month).padStart(2, "0");
  const lastDay = new Date(year, month, 0).getDate();
  const fromDate = date ? date : `${year}-${mm}-01`;
  const toDate = date ? date : `${year}-${mm}-${String(lastDay).padStart(2, "0")}`;

  // Fetch
  const [dailyEntries, vendors, cchs] = await Promise.all([
    dailyApi.list({ schoolId: sid, fromDate, toDate }),
    vendorApi.list(),
    cchApi.list(),
  ]);

  // Build a single "month" object for the picked month name. The builders
  // know how to expand `funds` / `grains` from daily entries via the
  // aggregator below; we feed an empty month and let aggregation populate.
  const monthName = MONTH_NAMES[month];
  const months = [createMonth(monthName)];

  const schoolInfo = {
    schoolName: schoolProfile.schoolName || "",
    schoolNameOdia: schoolProfile.schoolNameOdia || "",
    udiseCode: schoolProfile.udiseCode || "",
    district: schoolProfile.district || "",
    districtOdia: schoolProfile.districtOdia || "",
    block: schoolProfile.block || "",
    blockOdia: schoolProfile.blockOdia || "",
    village: schoolProfile.village || "",
    financialYear: ay,
    quarter: monthToQuarter(month),
    departmentName: schoolProfile.departmentName || "School & Mass Education Department",
    schemeName: schoolProfile.schemeName || "PM POSHAN",
    sanctionNoDate: "",
    reportDate: date || `${year}-${mm}-${String(lastDay).padStart(2, "0")}`,
    previousSanctionNoDate: "",
    previousSanctionedAmount: 0,
    previousUnspentBalance: 0,
    previousUtilizedAmount: 0,
  };

  const vendorInfo = defaultVendorInfo();
  // iter150 — auto-populate vendor picks from the school's master vendor
  // list so Hand Receipts show real vendor names/addresses out-of-the-box.
  // iter151 — prefer the vendor with the MOST complete address (post +
  // thana + pincode) so schools that have a partly-filled first vendor
  // and a fully-filled second one see the fully-filled one on receipts.
  const addressScore = (v) => (v.post ? 1 : 0) + (v.thana ? 1 : 0) + (v.pincode ? 1 : 0) +
                              (v.block ? 1 : 0) + (v.district ? 1 : 0) + (v.village ? 1 : 0) +
                              (v.fatherName ? 1 : 0);
  const pickFirst = (cat) => {
    const list = (vendors || []).filter((v) => (v.category || "").toLowerCase() === cat);
    if (!list.length) return null;
    // Sort by richest address descending, then by updatedAt (newest first).
    list.sort((a, b) => {
      const s = addressScore(b) - addressScore(a);
      if (s !== 0) return s;
      return String(b.updatedAt || "").localeCompare(String(a.updatedAt || ""));
    });
    return list[0];
  };
  const applyToInfo = (prefix, v) => {
    if (!v) return;
    vendorInfo[`${prefix}Vendor`] = v.nameOdia || v.name || "";
    vendorInfo[`${prefix}Father`] = v.fatherNameOdia || v.fatherName || "";
    vendorInfo[`${prefix}Relation`] = v.relationType === "husband" ? "husband" : "father";
    vendorInfo[`${prefix}Village`] = v.villageOdia || v.village || "";
    // Address = post/thana/block/district assembled (Odia if present else English).
    const parts = [
      v.postOdia || v.post, v.thanaOdia || v.thana, v.blockOdia || v.block, v.districtOdia || v.district,
    ].filter(Boolean);
    vendorInfo[`${prefix}Address`] = parts.length ? parts.join(", ") : (v.addressOdia || v.address || "");
    // iter151 — carry post / thana / pincode individually so the Odia Hand
    // Receipt template (Reports.jsx line ~138) can render each field
    // separately in the "ପୋଷ୍ଟ:__, ଥାନା:__, ପିନକୋଡ଼:__" line.
    vendorInfo[`${prefix}Post`] = v.post || "";
    vendorInfo[`${prefix}PostOdia`] = v.postOdia || "";
    vendorInfo[`${prefix}Thana`] = v.thana || "";
    vendorInfo[`${prefix}ThanaOdia`] = v.thanaOdia || "";
    vendorInfo[`${prefix}Block`] = v.block || "";
    vendorInfo[`${prefix}BlockOdia`] = v.blockOdia || "";
    vendorInfo[`${prefix}District`] = v.district || "";
    vendorInfo[`${prefix}DistrictOdia`] = v.districtOdia || "";
    vendorInfo[`${prefix}Pincode`] = v.pincode || "";
    vendorInfo[`${prefix}PincodeOdia`] = v.pincodeOdia || "";
    vendorInfo[`${prefix}FatherOdia`] = v.fatherNameOdia || "";
    vendorInfo[`${prefix}VillageOdia`] = v.villageOdia || "";
  };
  applyToInfo("grocery",   pickFirst("grocery"));
  applyToInfo("vegetable", pickFirst("vegetables") || pickFirst("vegetable"));
  applyToInfo("egg",       pickFirst("egg"));
  applyToInfo("fuel",      pickFirst("fuel"));
  applyToInfo("ladoo",     pickFirst("ladoo") || pickFirst("nutritionladoo") || pickFirst("nutrition_ladoo"));

  // Daily-driven aggregation
  const derivedMonths = aggregateDailyToMonthly(months, ay, dailyEntries);

  const monthlySummaries = buildMonthlySummaries(derivedMonths);
  const totals = buildTotals(derivedMonths, monthlySummaries);
  const annexureEntries = buildAnnexureEntries(derivedMonths, schoolInfo, vendorInfo, cchs, computeDryRation(dailyEntries));
  const odiaReceiptGroups = buildOdiaReceiptGroups(derivedMonths, schoolInfo, vendorInfo, cchs, vendors);
  const ingredientWiseReport = buildIngredientWiseReport(derivedMonths);
  const dryRationData = computeDryRation(dailyEntries);

  const ucScope = {
    mode: date ? "daily" : "monthly",
    label: date ? `Date ${date}` : `${monthName} ${year}`,
    periodNote: date ? `Daily UC for ${date}` : `Monthly UC for ${monthName} ${year}`,
  };

  // Day-wise ladoo breakdown for the Nutritious Ladoo + Ladoo Making
  // reports. Each row carries: date, day name (Tue/Thu/Sat), served count,
  // and per-ingredient { qty (gm/ml/pc), amount (Rs) }. Days that aren't
  // ladoo days OR have no served students are skipped.
  const dailyLadoo = buildDailyLadooBreakdown(dailyEntries || []);

  return {
    label: `${MONTH_NAMES[month]} ${year}${date ? ` · ${date}` : ""}`,
    schoolInfo, totals, monthlySummaries, annexureEntries,
    odiaReceiptGroups, ingredientWiseReport, dryRationData, ucScope,
    months: derivedMonths,
    dailyLadoo,
    cchList: cchs || [],
  };
}

// Same logic as Generator.jsx's dryRationData memo — extracted so reports
// can reuse it without dragging in the full Generator state machine.
function computeDryRation(dailyEntries) {
  return dryRationReportData(dailyEntries, { drGroupRice: !isClasswiseRiceNorms() });
}

// Wrap the rendered report HTML in Office-compatible boilerplate so Word /
// Excel open it cleanly. Both formats happily ingest HTML when the right
// content-type extension is used + a minimal head section is added.
function wrapForOffice(html, schoolInfo, title, kind) {
  const headerNote = `<h2 style="font-family:Calibri,Arial,sans-serif;">${title}</h2>
    <p style="font-family:Calibri,Arial,sans-serif;color:#555;">
      ${schoolInfo.schoolName} · UDISE ${schoolInfo.udiseCode} · FY ${schoolInfo.financialYear}
    </p>`;
  if (kind === "word") {
    return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="utf-8"/><title>${title}</title>
      <style>
        body{font-family:Calibri,Arial,sans-serif;}
        table{border-collapse:collapse;width:100%;}
        th,td{border:1px solid #111;padding:4px;}
        .no-print{display:none;}
      </style>
      </head><body>${headerNote}${html}</body></html>`;
  }
  // Excel
  return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
    <head><meta charset="utf-8"/><title>${title}</title>
    <style>body{font-family:Calibri,Arial,sans-serif;}table{border-collapse:collapse;}th,td{border:1px solid #111;padding:4px;}.no-print{display:none;}</style>
    </head><body>${headerNote}${html}</body></html>`;
}
